import {useQuery, useQueryClient} from '@tanstack/react-query';
import {useCallback} from 'react';

import {usePanel} from '../../app/PanelProvider.js';

export const PRIVACY_LIST_KEY = ['privacy', 'requests'] as const;
export const privacyRequestKey = (requestId: string) =>
  ['privacy', 'request', requestId] as const;

export const usePrivacyRequests = () => {
  const {dataSource} = usePanel();
  return useQuery({
    queryKey: PRIVACY_LIST_KEY,
    queryFn: ({signal}) => dataSource.listPrivacyRequests(signal),
  });
};

/**
 * O detalhe de um pedido.
 *
 * Fica desligado enquanto ninguém escolheu um pedido: buscar o detalhe de um
 * identificador vazio geraria uma requisição que só pode terminar em 404, e um
 * 404 na tela seria lido como "o pedido sumiu".
 */
export const usePrivacyRequest = (requestId: string | null) => {
  const {dataSource} = usePanel();
  return useQuery({
    queryKey: privacyRequestKey(requestId ?? ''),
    queryFn: ({signal}) => dataSource.getPrivacyRequest(requestId!, signal),
    enabled: requestId !== null,
  });
};

/**
 * Recarrega lista e detalhe juntos.
 *
 * Depois de verificar ou executar, os dois mudam ao mesmo tempo. Recarregar só
 * um deixaria a lista dizendo `aguardando verificação` ao lado de um detalhe
 * já verificado, e quem olha não tem como saber qual dos dois está velho.
 */
export const usePrivacyRefresh = (requestId: string | null) => {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({queryKey: PRIVACY_LIST_KEY});
    if (requestId !== null) {
      void queryClient.invalidateQueries({
        queryKey: privacyRequestKey(requestId),
      });
    }
  }, [queryClient, requestId]);
};
